// Text model of a Willow Create visual: the HTML is parsed without layout, so block-level
// children are recognised by tag rather than by computed style as the in-iframe editor does.
const TEXT_SELECTOR = "h1,h2,h3,h4,h5,h6,p,span,li,a,div,b,strong,em,small,blockquote";
const INLINE_TAGS = new Set(["SPAN", "A", "B", "STRONG", "EM", "I", "SMALL", "BR", "SUB", "SUP", "MARK", "U", "S", "CODE", "LABEL"]);

export interface VisualSlide {
  index: number;
  texts: string[];
}

const parseVisual = (html: string): Document => new DOMParser().parseFromString(html, "text/html");

const getPanes = (doc: Document): Element[] => {
  const sections = Array.from(doc.querySelectorAll("body > section"));
  return sections.length > 0 ? sections : [doc.body];
};

const hasBlockChild = (element: Element): boolean => Array.from(element.children).some(child => !INLINE_TAGS.has(child.tagName));

const isTextElement = (element: Element): boolean =>
  !!(element.textContent ?? "").trim() && !element.querySelector("img,svg") && !hasBlockChild(element) && !element.closest("script,style");

const collectTextElements = (pane: Element): Element[] => {
  const candidates = Array.from(pane.querySelectorAll(TEXT_SELECTOR)).filter(isTextElement);
  return candidates.filter(element => !candidates.some(other => other !== element && other.contains(element)));
};

const readText = (element: Element): string => {
  let text = "";
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += (node.textContent ?? "").replace(/[ \t\n\r\f]+/g, " ");
    } else if (node.nodeName === "BR") {
      text += "\n";
    } else {
      Array.from(node.childNodes).forEach(walk);
    }
  };
  walk(element);
  return text
    .split("\n")
    .map(line => line.trim())
    .join("\n")
    .trim();
};

const escapeHtml = (text: string): string => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const serialize = (doc: Document): string => `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;

export const extractVisualTexts = (html: string): VisualSlide[] =>
  getPanes(parseVisual(html)).map((pane, index) => ({ index, texts: collectTextElements(pane).map(readText) }));

// Returns the edited document, or null when the address does not exist in this visual.
export const setVisualText = (html: string, slide: number, index: number, text: string): string | null => {
  const doc = parseVisual(html);
  const pane = getPanes(doc)[slide];
  if (!pane) return null;
  const element = collectTextElements(pane)[index];
  if (!element) return null;
  element.innerHTML = escapeHtml(text.trim()).replace(/[ \t]*\n[ \t]*/g, "<br>");
  // Same as the in-place editor's export: a long word wraps instead of bleeding past the canvas edge.
  (element as HTMLElement).style.setProperty("overflow-wrap", "break-word");
  (element as HTMLElement).style.setProperty("hyphens", "auto");
  return serialize(doc);
};

export const formatVisualTexts = (slides: VisualSlide[], clippedSlides: number[] | null): string => {
  const lines = slides.map(slide =>
    [`Slide ${slide.index + 1}:`, ...slide.texts.map((text, index) => `  [${slide.index}.${index}] ${text.replace(/\n/g, " / ")}`)].join("\n")
  );
  const overflow =
    clippedSlides && clippedSlides.length > 0
      ? `\nText overflows the canvas on slide ${clippedSlides.map(index => index + 1).join(", ")} and will be cut off; shorten it.`
      : "";
  return `${slides.length} slide${slides.length === 1 ? "" : "s"}.\n${lines.join("\n")}${overflow}`;
};
